import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "../i18n";

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "🇺🇸"
  },
  {
    code: "fr",
    name: "French",
    nativeName: "Français",
    flag: "🇫🇷"
  },
  {
    code: "rw",
    name: "Kinyarwanda",
    nativeName: "Ikinyarwanda",
    flag: "🇷🇼"
  }
];

const LanguageSelector: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already selected a language
    const hasSelectedLanguage = localStorage.getItem("language-selected");
    const savedLanguage = localStorage.getItem("i18nextLng");
    
    // Show modal if no language has been selected and no saved language exists
    if (!hasSelectedLanguage && !savedLanguage) {
      setShowModal(true);
    }
  }, []);

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);
  };

  const handleConfirm = () => {
    if (selectedLanguage) {
      // Save language preference
      localStorage.setItem("language-selected", "true");
      localStorage.setItem("i18nextLng", selectedLanguage);
      
      // Change i18n language
      i18n.changeLanguage(selectedLanguage);
      
      // Close modal
      setShowModal(false);
      
      // Navigate to appropriate docs page
      navigate(`/${selectedLanguage}/docs`);
    }
  };

  const handleSkip = () => {
    // Set default to English and close modal
    localStorage.setItem("language-selected", "true");
    localStorage.setItem("i18nextLng", "en");
    i18n.changeLanguage("en");
    setShowModal(false);
  };

  // Function to reset language selection (for testing or admin purposes)
  // const resetLanguageSelection = () => {
  //   localStorage.removeItem("language-selected");
  //   setShowModal(true);
  // };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 shadow-xl">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Choose Your Language
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Select your preferred language for the documentation
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageSelect(language.code)}
              className={`w-full p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedLanguage === language.code
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{language.flag}</span>
                <div className="text-left">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {language.nativeName}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {language.name}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleSkip}
            className="flex-1 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedLanguage}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedLanguage
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector;
